import {useAppSelector} from '@/redux-store/store';
import {Suspense, useEffect, useState} from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams
} from 'react-router-dom';
import Home from './pages/home';
import NotFound from './pages/not-found';
import LoadingScreen from './pages/loading-screen';
import Redirect from './pages/auth/redirect';
import ForbiddenPage from './pages/forbidden-page';
import AdminCompanyDetailsPage from './pages/admin/company-details-page';
import AdminMainPage from './pages/admin/main-page';
import MatrixRedirect from './pages/matrix/redirect';
import OnboardingRedirect from './pages/payments/onboarding-redirect';
import {env} from './config';
import {useDispatch} from 'react-redux';
import {Sport} from './types/common';
import {CITY_COORDINATES, CompanyRole} from './constants';
import {setLocations, setSports} from './redux-store/slices/filter';
import {
  purgeAppTag,
  setCurrentCompanyId,
  setIsPublicCompany
} from './redux-store/slices/auth';
import RequireAuthenticationPage from './pages/require-authentication-page';
import EmployeeManagementPage from './pages/employee-management-page';
import {getPublicCompanyId, isAllowedTag, isPublicCompany} from './utils';

const {APP_VARIANT, TAG_PAIRS, PUBLIC_COMPANIES} = env;

const ValidateUrlIdentifier: React.FC<{
  children: React.ReactNode;
  sports: Record<string, Sport>;
  appTag: string | null;
  authenticated: boolean;
  isSjOne: boolean;
}> = ({children, sports, appTag, authenticated, isSjOne}) => {
  const {identifier} = useParams<{identifier: string}>();
  const dispatch = useDispatch();
  const [status, setStatus] = useState<
    'loading' | 'ok' | 'forbidden' | 'notfound'
  >('loading');

  useEffect(() => {
    if (Object.keys(sports).length === 0) {
      setStatus('loading');
      return;
    }

    const sportsKeys = Object.values(sports).map((sports) => sports.key);

    if (!identifier) {
      setStatus(isSjOne && !authenticated ? 'forbidden' : 'notfound');
      return;
    }

    if (isPublicCompany(identifier, PUBLIC_COMPANIES) && isSjOne) {
      const companyId = getPublicCompanyId(identifier, PUBLIC_COMPANIES);
      dispatch(setCurrentCompanyId(companyId));
      dispatch(setIsPublicCompany(true));
      setStatus('ok');
    } else if (Object.keys(CITY_COORDINATES).includes(identifier)) {
      const {lat, lng, label} =
        CITY_COORDINATES[identifier as keyof typeof CITY_COORDINATES];
      if (appTag) {
        dispatch(purgeAppTag());
      }
      dispatch(setLocations({longitude: lng, latitude: lat, label}));
      setStatus('ok');
    } else if (sportsKeys.includes(identifier)) {
      if (appTag) {
        dispatch(purgeAppTag());
      }
      const foundSport = Object.values(sports).find(
        (s) => s.key === identifier
      );
      const sportId = foundSport?._id;
      if (sportId) {
        dispatch(setSports(sportId));
        setStatus('ok');
      } else {
        setStatus(isSjOne && !authenticated ? 'forbidden' : 'notfound');
      }
    } else if (isAllowedTag(identifier, TAG_PAIRS)) {
      setStatus('ok');
    } else {
      setStatus(isSjOne && !authenticated ? 'forbidden' : 'notfound');
    }
  }, [identifier, dispatch, authenticated, isSjOne, sports, appTag]);

  if (status === 'loading') {
    return <LoadingScreen />;
  }
  if (status === 'forbidden') {
    return <ForbiddenPage />;
  }
  if (status === 'notfound') {
    return <NotFound />;
  }
  return <>{children}</>;
};
export const useAuth = () => {
  const {isAuthenticated, appTag, isPublicCompany, keycloakReady} =
    useAppSelector((state) => state.auth);
  const {sports} = useAppSelector((state) => state.sports);
  const {isLoading} = useAppSelector((state) => state.auth);
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);

  const companies = useAppSelector(
    (state) => state.userInfos.userInfos.companies || []
  );
  const companyRole = companies.length === 1 ? companies[0].role : null;

  return {
    isAuthenticated,
    isLoading,
    userRole,
    companyRole,
    sports,
    appTag,
    isPublicCompany,
    keycloakReady
  };
};

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  isLoading: boolean;
  keycloakReady: boolean;
  isAuthenticated: boolean;
  isPublicCompany: boolean;
  isSjOne: boolean;
}> = ({
  children,
  isLoading,
  keycloakReady,
  isAuthenticated,
  isSjOne,
  isPublicCompany
}) => {
  if (isLoading || !keycloakReady) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated && isSjOne && !isPublicCompany) {
    return <RequireAuthenticationPage />;
  }

  return isAuthenticated || isPublicCompany ? (
    <>{children}</>
  ) : (
    <Navigate to="/403" />
  );
};

const OnlyHealthManagerOrAdminRoute: React.FC<{
  children: React.ReactNode;
  isLoading: boolean;
  companyRole: CompanyRole | null;
  userRole: string | undefined;
}> = ({children, isLoading, companyRole, userRole}) => {
  if (isLoading) {
    return <LoadingScreen />;
  }

  return companyRole === 'HEALTH_MANAGER' || userRole === 'ADMIN' ? (
    <>{children}</>
  ) : (
    <Navigate to="/403" />
  );
};

const Router: React.FC = () => {
  const isSjOne = APP_VARIANT === 'sjone';
  const {
    isAuthenticated,
    isLoading,
    keycloakReady,
    companyRole,
    sports,
    appTag,
    userRole,
    isPublicCompany
  } = useAuth();

  return (
    <Suspense fallback={<LoadingScreen />}>
      <BrowserRouter>
        <Routes>
          <Route path="/home" element={<Home />} />
          <Route
            path="/:identifier"
            element={
              <ValidateUrlIdentifier
                sports={sports}
                appTag={appTag}
                authenticated={isAuthenticated}
                isSjOne={isSjOne}
              >
                {isSjOne ? (
                  <ProtectedRoute
                    isLoading={isLoading}
                    isAuthenticated={isAuthenticated}
                    isPublicCompany={isPublicCompany}
                    isSjOne={isSjOne}
                    keycloakReady={keycloakReady}
                  >
                    <Home />
                  </ProtectedRoute>
                ) : (
                  <Home />
                )}
              </ValidateUrlIdentifier>
            }
          />
          <Route
            path="/event/:eventId"
            element={
              isSjOne ? (
                <ProtectedRoute
                  isLoading={isLoading}
                  isAuthenticated={isAuthenticated}
                  isPublicCompany={isPublicCompany}
                  isSjOne={isSjOne}
                  keycloakReady={keycloakReady}
                >
                  <Home />
                </ProtectedRoute>
              ) : (
                <Home />
              )
            }
          />
          <Route path="/on-demand/:eventId" element={<Home />} />

          <Route
            path="/group/:groupId"
            element={
              isSjOne ? (
                <ProtectedRoute
                  isLoading={isLoading}
                  isAuthenticated={isAuthenticated}
                  isPublicCompany={isPublicCompany}
                  isSjOne={isSjOne}
                  keycloakReady={keycloakReady}
                >
                  <Home />
                </ProtectedRoute>
              ) : (
                <Home />
              )
            }
          />

          <Route
            path="/:identifier/event/:eventId"
            element={
              <ValidateUrlIdentifier
                sports={sports}
                appTag={appTag}
                authenticated={isAuthenticated}
                isSjOne={isSjOne}
              >
                {isSjOne ? (
                  <ProtectedRoute
                    isLoading={isLoading}
                    isAuthenticated={isAuthenticated}
                    isPublicCompany={isPublicCompany}
                    isSjOne={isSjOne}
                    keycloakReady={keycloakReady}
                  >
                    <Home />
                  </ProtectedRoute>
                ) : (
                  <Home />
                )}
              </ValidateUrlIdentifier>
            }
          />
          <Route
            path="/:identifier/group/:groupId"
            element={
              <ValidateUrlIdentifier
                sports={sports}
                appTag={appTag}
                authenticated={isAuthenticated}
                isSjOne={isSjOne}
              >
                {isSjOne ? (
                  <ProtectedRoute
                    isLoading={isLoading}
                    isAuthenticated={isAuthenticated}
                    isPublicCompany={isPublicCompany}
                    isSjOne={isSjOne}
                    keycloakReady={keycloakReady}
                  >
                    <Home />
                  </ProtectedRoute>
                ) : (
                  <Home />
                )}
              </ValidateUrlIdentifier>
            }
          />
          <Route
            path="/:identifier/event/:eventId/groupId"
            element={
              <ValidateUrlIdentifier
                sports={sports}
                appTag={appTag}
                authenticated={isAuthenticated}
                isSjOne={isSjOne}
              >
                {isSjOne ? (
                  <ProtectedRoute
                    isLoading={isLoading}
                    isAuthenticated={isAuthenticated}
                    isPublicCompany={isPublicCompany}
                    isSjOne={isSjOne}
                    keycloakReady={keycloakReady}
                  >
                    <Home />
                  </ProtectedRoute>
                ) : (
                  <Home />
                )}
              </ValidateUrlIdentifier>
            }
          />
          <Route
            path="/:identifier/group/:groupId/:eventId"
            element={
              <ValidateUrlIdentifier
                sports={sports}
                appTag={appTag}
                authenticated={isAuthenticated}
                isSjOne={isSjOne}
              >
                {isSjOne ? (
                  <ProtectedRoute
                    isLoading={isLoading}
                    isAuthenticated={isAuthenticated}
                    isPublicCompany={isPublicCompany}
                    isSjOne={isSjOne}
                    keycloakReady={keycloakReady}
                  >
                    <Home />
                  </ProtectedRoute>
                ) : (
                  <Home />
                )}
              </ValidateUrlIdentifier>
            }
          />
          <Route path="/auth/redirect" element={<Redirect />} />
          <Route path="/matrix/redirect" element={<MatrixRedirect />} />
          <Route path="/auth/initredirect" element={<Redirect />} />
          <Route
            path="/onboarding-redirect/:mode/:eventId?"
            element={<OnboardingRedirect />}
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute
                isLoading={isLoading}
                isAuthenticated={isAuthenticated}
                isPublicCompany={isPublicCompany}
                isSjOne={isSjOne}
                keycloakReady={keycloakReady}
              >
                <OnlyHealthManagerOrAdminRoute
                  isLoading={isLoading}
                  companyRole={companyRole}
                  userRole={userRole}
                >
                  <AdminMainPage />
                </OnlyHealthManagerOrAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/company/:companyId"
            element={
              <ProtectedRoute
                isLoading={isLoading}
                isAuthenticated={isAuthenticated}
                isPublicCompany={isPublicCompany}
                isSjOne={isSjOne}
                keycloakReady={keycloakReady}
              >
                <OnlyHealthManagerOrAdminRoute
                  isLoading={isLoading}
                  companyRole={companyRole}
                  userRole={userRole}
                >
                  <AdminCompanyDetailsPage />
                </OnlyHealthManagerOrAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/company"
            element={
              <ProtectedRoute
                isLoading={isLoading}
                isAuthenticated={isAuthenticated}
                isPublicCompany={isPublicCompany}
                isSjOne={isSjOne}
                keycloakReady={keycloakReady}
              >
                <OnlyHealthManagerOrAdminRoute
                  isLoading={isLoading}
                  companyRole={companyRole}
                  userRole={userRole}
                >
                  <Navigate to="/admin" />
                </OnlyHealthManagerOrAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/company-management"
            element={
              <ProtectedRoute
                isLoading={isLoading}
                isAuthenticated={isAuthenticated}
                isPublicCompany={isPublicCompany}
                isSjOne={isSjOne}
                keycloakReady={keycloakReady}
              >
                <OnlyHealthManagerOrAdminRoute
                  isLoading={isLoading}
                  companyRole={companyRole}
                  userRole={userRole}
                >
                  <EmployeeManagementPage />
                </OnlyHealthManagerOrAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="/" element={<Navigate to="/home" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

export default Router;
